import { Box, Typography } from '@mui/material';
import { AssetType } from 'src/types/assets';
import { FloatItem } from '../floatItem';
import { Label } from './components';
import { waterElement, waterLevelLabel, emptyWell, StyledChildrenBox } from './style';
import { useData } from './hooks';
import { useResponsive } from 'src/hooks/useResponsive';

type AlarmDataItem = {
  id: string;
  alarmTagName: string;
  assignedAlarmTagId: string;
  assignedAlarmGatewayId: string;
  level: number;
};

type Props = {
  children: React.ReactNode;
  asset: AssetType | undefined;
  heightWell: number;
  waterLevel: number | null;
  alarmData: AlarmDataItem[];
  unit: string;
  widget: any;
  isWetWell: boolean;
  isTriplexPump: boolean;
  isQuadroplexPump: boolean;
};

export const EmptyWell = ({
  children,
  asset,
  waterLevel,
  heightWell,
  alarmData,
  unit,
  widget,
  isWetWell,
  isTriplexPump,
  isQuadroplexPump,
}: Props) => {
  const isMobile = useResponsive('down', 600);
  const isTablet = useResponsive('between', 601, 1025);

  const height = isMobile ? '282px' : isTablet ? '310px' : '400px';
  const width = isMobile ? '245px' : isTablet ? '400px' : '500px';
  const positionBetweenFloatItems = isMobile ? 8 : 15;

  const { readingValue } = useData({ asset: asset });

  const waterLevelData = waterLevel ?? Number(readingValue);
  const waterPercentage =
    heightWell > 0 ? Math.min(100, Math.max(0, (waterLevelData / heightWell) * 100)) : 0;

  const assetPropertyTagNames = widget?.connectedTo?.wetWell?.assetProperties?.map(
    (item: any) => item.tagName
  );

  const filteredAlarmData = alarmData?.filter((alarm) =>
    assetPropertyTagNames.includes(alarm.alarmTagName)
  );

  const sortedFilteredAlarmData = filteredAlarmData?.sort((a, b) => a.level - b.level);

  return (
    <Box
      sx={{
        height: height,
        maxWidth: width,
        ...emptyWell,
      }}
    >
      {sortedFilteredAlarmData?.map((item) => {
        const topPercent = heightWell > 0 ? 100 - (item.level / heightWell) * 100 : 0;
        return (
          <Label
            key={item.id}
            label={{ name: item.alarmTagName, value: item.level }}
            topPosition={`${topPercent}%`}
          />
        );
      })}

      {sortedFilteredAlarmData?.map((item, index) => {
        const floatTop = heightWell > 0 ? `${100 - (item.level / heightWell) * 100}%` : '0%';
        const leftPosition = 10 + index * positionBetweenFloatItems;

        const isUnderTheWater = Number(item?.level) <= Number(waterLevelData);

        return (
          <FloatItem
            key={item.id}
            floatTop={floatTop}
            leftPosition={leftPosition}
            isUnderTheWater={isUnderTheWater}
          />
        );
      })}

      <Box
        sx={{
          height: `${waterPercentage}%`,
          ...waterElement,
        }}
      >
        {isWetWell && (
          <Typography
            sx={{
              ...waterLevelLabel,
              ...(isMobile &&
                isQuadroplexPump && {
                  right: 'auto',
                  left: '100%',
                  marginLeft: '4px',
                  whiteSpace: 'nowrap',
                }),
            }}
            data-sm="level"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {!isNaN(waterLevelData) && (
                <>
                  {waterLevelData} <span>{unit || 'ft'}</span>
                </>
              )}
            </Box>
          </Typography>
        )}
      </Box>

      <StyledChildrenBox
        isMobile={isMobile}
        isTablet={isTablet}
        isTriplexPump={isTriplexPump}
        isQuadroplexPump={isQuadroplexPump}
      >
        {children}
      </StyledChildrenBox>
    </Box>
  );
};
